import { Keyboard, PixelRatio, Platform, StyleSheet, View } from "react-native";
import React, { useCallback, useState, useMemo, useEffect, useImperativeHandle } from "react";
import { DateTimePickerProps } from "react-native-modal-datetime-picker";
import { DatePicker } from "./DatePicker";
import BaseField, { BaseFieldProps } from "./BaseField";
import { useTheme } from "../hooks/use-theme";
import Text from "./Text";
import TimeField from "./TimeField";
import { minutesAsTimeString } from "../utils";
import { useDateFormatter } from "../hooks/date";
import { PressableWithFocus } from "./PressableWithFocus";
import { useDropdown } from "../hooks/use-dropdown";
import { DropdownComponentType } from "../contexts/DropdownManager";
import { hasNativeDatePicker } from "./DatePicker/DatePicker";
import { IOS_MINIMUM_TIME_OPACITY } from "./TimeField/utils";

const FONT_SCALE = PixelRatio.getFontScale();

const BASE_TIME_TEXT_LINE_HEIGHT = 28;
const SCALED_TIME_TEXT_LINE_HEIGHT =
    BASE_TIME_TEXT_LINE_HEIGHT - (FONT_SCALE - 1) * (BASE_TIME_TEXT_LINE_HEIGHT / 1.5);

const BASE_TIME_CONTAINER_MIN_WIDTH = 62;
const SCALED_TIME_CONTAINER_MIN_WIDTH = BASE_TIME_CONTAINER_MIN_WIDTH * FONT_SCALE;

type IOSDisplay = "default" | "compact" | "inline" | "spinner";

type DateFieldProps = Omit<BaseFieldProps, "component" | "hasValue" | "value" | "disabled"> &
    Omit<DateTimePickerProps, "onConfirm" | "onCancel" | "date"> & {
        label?: string;
        minimumDate?: Date;
        maximumDate?: Date;
        setDateFn?: string;
        display?: IOSDisplay;
        disabled?: boolean;
        date?: Date | null;
        value?: Date | null;
        onChange?: (date: Date | null) => void;
        timePicker?: boolean | `${number}:${number}`; // Can either gives true or a default time (ex. 07:30). If time is not defined, the default will be 0:00
    };

type DateFieldHandle = {
    focus: () => void;
};

const buildZuluDate = (baseDate: Date, h: number = 0, m: number = 0, s: number = 0): Date => {
    const year = baseDate.getFullYear();
    const month = `${baseDate.getMonth() + 1}`.padStart(2, "0");
    const day = `${baseDate.getDate()}`.padStart(2, "0");
    const hours = `${h}`.padStart(2, "0");
    const minutes = `${m}`.padStart(2, "0");
    const seconds = `${s}`.padStart(2, "0");

    // new date is now in UTC
    return new Date(`${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`);
};

const DateField = ({
    value,
    onChange,
    onBlur,
    label,
    placeholder,
    date,
    isSelfContained,
    minimumDate,
    maximumDate,
    style,
    error,
    left,
    right = !left ? "calendar" : undefined,
    disabled,
    setDateFn,
    display = "inline",
    testID,
    timePicker = false,
    ref,
    ...props
}: DateFieldProps & { ref?: React.Ref<DateFieldHandle> }) => {
    const { gridSize } = useTheme();
    const [showDateChooser, setShowDateChooser] = useState(false);
    const [timeTmp, setTimeTmp] = useState<number | undefined>(
        typeof timePicker === "string"
            ? timePicker
                  .split(":")
                  .map((n) => parseInt(n))
                  .reduce((result, t, index) => result + t * (index === 0 ? 60 : 1), 0)
            : undefined,
    );

    const handleNewDate = useCallback(
        (inputDate: Date | null) => {
            if (inputDate) {
                // extract only the information we need
                let newDate = buildZuluDate(inputDate);
                if (timeTmp) {
                    const [h, m] = minutesAsTimeString(timeTmp)
                        .split(":")
                        .map((n) => parseInt(n));
                    newDate = buildZuluDate(inputDate, h, m, 0);
                }

                setShowDateChooser(false);

                if (minimumDate && newDate < minimumDate) {
                    // DateTimePickerModal will return today's date if user just click confirm.
                    // So we need to check if the date is not before the minimum date
                    onChange?.(minimumDate);
                    return;
                }
                onChange?.(newDate);
            } else {
                onChange?.(null);
            }
        },
        [minimumDate, onChange, timeTmp],
    );

    const handleNewTime = useCallback(
        (time: number) => {
            setTimeTmp(time);

            if (value) {
                const [h, m] = minutesAsTimeString(time)
                    .split(":")
                    .map((n) => parseInt(n));
                const newDate = buildZuluDate(value, h, m, 0);
                onChange?.(newDate);
            }
        },
        [onChange, value],
    );

    useEffect(() => {
        if (Platform.OS === "web" && setDateFn) {
            const fnName = `set${setDateFn}`;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window[fnName] = (date: string) => {
                onChange?.(new Date(date));
            };

            return () => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                window[fnName] = null;
            };
        }
    }, [onChange, setDateFn]);

    const formatDate = useDateFormatter();

    const focus = useCallback(() => {
        setShowDateChooser(true);
    }, []);

    useImperativeHandle(ref, () => ({ focus }));

    const [isTimeFocused, setIsTimeFocused] = useState(false);

    const formattedDateInRange = useMemo(() => {
        if (value) {
            return value;
        }

        const today = date || new Date();
        if (minimumDate && today < minimumDate) {
            return minimumDate;
        } else if (maximumDate && today > maximumDate) {
            return maximumDate;
        }

        return today;
    }, [date, maximumDate, minimumDate, value]);

    const datePickerProps = useMemo<DateTimePickerProps>(
        () => ({
            date: formattedDateInRange,
            display,
            isVisible: showDateChooser,
            onConfirm: (d) => {
                handleNewDate(d);
                onBlur?.();
            },
            onCancel: () => {
                setShowDateChooser(false);
                onBlur?.();
            },
            minimumDate,
            maximumDate,
            // ensure timezone is not altering date value
            ...props,
        }),
        [
            formattedDateInRange,
            display,
            showDateChooser,
            minimumDate,
            maximumDate,
            props,
            handleNewDate,
            onBlur,
        ],
    );

    const dropdownComponent = useCallback<DropdownComponentType>(
        ({ fadeOut }) => (
            <DatePicker
                {...datePickerProps}
                onConfirm={(d) => {
                    handleNewDate(d);
                    fadeOut();
                    onBlur?.();
                }}
                onCancel={() => {
                    setShowDateChooser(false);
                    fadeOut();
                    onBlur?.();
                }}
            />
        ),
        [datePickerProps, handleNewDate, onBlur],
    );

    const { ref: dropdownRef, onPress: onDropdownPress } = useDropdown({
        component: dropdownComponent,
        yMargin: gridSize / 2,
        position: !timePicker ? "left" : "right",
        onBlur: () => {
            setShowDateChooser(false);
        },
    });

    const toggle = useCallback(() => {
        Keyboard.dismiss();
        setShowDateChooser(!showDateChooser);
        if (!hasNativeDatePicker) {
            onDropdownPress();
        }
    }, [onDropdownPress, showDateChooser]);

    return (
        <>
            <BaseField
                componentRef={dropdownRef}
                component={
                    <PressableWithFocus disabled={disabled} onPress={toggle}>
                        {timePicker && (
                            <TimePicker
                                value={timeTmp || 0}
                                onChange={handleNewTime}
                                disabled={disabled}
                                onFocus={() => {
                                    setIsTimeFocused(true);
                                }}
                                onBlur={() => {
                                    setIsTimeFocused(false);
                                }}
                            />
                        )}
                        {hasNativeDatePicker && <DatePicker {...datePickerProps} />}
                    </PressableWithFocus>
                }
                value={
                    value
                        ? formatDate(value, {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                          })
                        : ""
                }
                isFocused={showDateChooser || isTimeFocused}
                label={label}
                placeholder={placeholder}
                left={left}
                right={right}
                error={error}
                testID={testID}
                disabled={disabled}
                style={[style, isTimeFocused && { zIndex: 1 }]}
                isSelfContained={isSelfContained}
                asButton
            />
        </>
    );
};

type TimePickerProps = {
    value: number;
    onChange: (time: number) => void;
    onFocus: () => void;
    onBlur: () => void;
    disabled?: boolean;
};

const TimePicker = ({ value, disabled, ...props }: TimePickerProps) => {
    const styles = useStyles();
    const [hours, minutes] = useMemo(() => minutesAsTimeString(value).split(":"), [value]);

    return (
        <View style={[styles.container, disabled && styles.disabled]}>
            <Text style={styles.text}>
                {hours.padStart(2, "0")}:{minutes.padStart(2, "0")}
            </Text>
            <TimeField
                value={value}
                disabled={disabled}
                style={styles.timeField}
                popupFallback
                {...props}
            />
        </View>
    );
};

const useStyles = () => {
    const { fonts, colors } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    position: "absolute",
                    zIndex: 1000,
                    right: 0,
                    backgroundColor: colors.body,
                    height: 28,
                    paddingHorizontal: 7,
                    minWidth: SCALED_TIME_CONTAINER_MIN_WIDTH,
                    borderRadius: 14,
                    alignItems: "center",
                    justifyContent: "center",
                },
                disabled: {
                    backgroundColor: colors.lighterGrey,
                },
                text: {
                    ...fonts.bodyLarge,
                    fontSize: 14,
                    lineHeight: SCALED_TIME_TEXT_LINE_HEIGHT,
                    alignItems: "center",
                    justifyContent: "center",
                    textAlignVertical: "center",
                    letterSpacing: 1.4,
                    color: colors.background,
                },
                timeField: {
                    position: "absolute",
                    paddingHorizontal: 0,
                    height: "100%",
                    width: "100%",
                    borderWidth: 0,
                    opacity: IOS_MINIMUM_TIME_OPACITY,
                },
            }),
        [colors.background, colors.body, colors.lighterGrey, fonts.bodyLarge],
    );
};

export default DateField;
